// Lo que se guarda en `agent_chats.lienzo` — ADR-2026-09-16-el-lienzo-se-guarda-por-chat.
//
// Un jsonb por chat, leído con normalización defensiva (el mismo criterio que
// `descarga_config`): un jsonb viejo, ajeno o corrupto nunca rompe el lienzo, cae al
// acomodo automático. `null` en la columna significa exactamente eso.

import type { Trazo } from '../../pages/flujos/graph/types';
import {
  ACCIONES_DE_DISENO,
  RESULTADOS_DE_MEZCLA,
  normalizarDiseno,
  DISENO_VACIO,
  type AccionDeDiseno,
  type DisenoGuardado,
  type ResultadoDeMezcla,
} from '../../pages/v2/modes/disenoIndumentaria/disenoGuardado';
import type { Posiciones, Punto } from './acomodo';
import { normalizarMarca, type Marca } from '../../pages/v2/modes/disenoIndumentaria/marcador';

/**
 * Una tanda que pidió el usuario desde el visor prestado (Carrusel, Animar, Expansión) y
 * que no está en ningún mensaje del chat: el lienzo es su única fuente, así que se guarda
 * (ADR-2026-09-16-las-tandas-del-visor-aterrizan-en-el-lienzo). Se guarda APENAS se pide,
 * con `pendiente`, y se completa con sus ids cuando llegan —o, si el chat se cerró en el
 * medio, cuando se vuelve a abrir y se los busca en la base
 * (ADR-2026-09-16-las-tandas-en-vuelo-se-guardan-y-se-reanudan).
 */
export interface TandaGuardada {
  id: string;
  titulo: string;
  /** Epoch del pedido. */
  creadaEn: number;
  ratio: number;
  esVideo: boolean;
  /** La imagen del lienzo desde la que se pidió: la tanda se acomoda debajo de ella. */
  origenId?: string;
  /**
   * Dónde aterrizó (esquina superior izquierda de su grilla), decidido una vez al
   * pedirla: así no sigue a su foto de origen cuando la mueven
   * (ADR-2026-09-17-la-tanda-aterriza-una-vez). Las guardadas antes no lo tienen y se
   * acomodan como siempre.
   */
  posicion?: Punto;
  imageIds: string[];
  /** Todavía en vuelo: cuántos recuadros mostrar. Se borra al resolver. Con
   *  `executionId` (las tandas del modo Diseño, que generan por `creativo_plus` con un
   *  id que el front conoce) se reanuda buscando por `execution_id`; con `executionIds`,
   *  por varios (Mostrar en uso: un pedido por foto —
   *  ADR-2026-09-18-mostrar-en-uso-escribe-sus-prompts); sin ninguno, por origen. */
  pendiente?: { total: number; executionId?: string; executionIds?: string[]; pedido?: PedidoGuardado };
}

/**
 * Lo que hace falta para volver a armar un pedido del modo Diseño con los mismos ids si
 * el backend nunca lo recibió (la página se recargó mientras la visión escribía el
 * prompt) — ADR-2026-09-18-una-tanda-en-vuelo-se-relanza-si-el-pedido-no-llego. Sólo ids
 * de celdas y texto: las URLs se vuelven a firmar al relanzar.
 */
export interface PedidoGuardado {
  accion: AccionDeDiseno;
  celdaId?: string;
  partes?: { celdaId: string; instruccion: string; marca?: Marca }[];
  instruccion?: string;
  instruccionGeneral?: string;
  /** Mezcla: qué tipo de imagen sale (ADR-2026-09-18-la-mezcla-elige-realista-o-como-la-base). */
  resultado?: ResultadoDeMezcla;
  /** Mezcla: lo pintado sobre la base, dónde va el cambio (ADR-2026-09-21-el-marcador-de-la-mezcla). */
  marcaBase?: Marca;
  textoBase?: string;
  cantidad: number;
}

export const TIPOS_DE_ANOTACION = ['texto', 'nota', 'comentario'] as const;
export type TipoDeAnotacion = (typeof TIPOS_DE_ANOTACION)[number];

/**
 * Una anotación del lienzo: los nodos de texto, nota y comentario de la barra de Flujos
 * (ADR-2026-09-16-el-lienzo-toma-la-barra-de-flujos). Se guarda lo que React Flow
 * necesita para volver a dibujarla y lo que el nodo lee (`params`); nunca `selected`,
 * `measured` ni `dragging`, que son de la sesión.
 */
export interface Anotacion {
  id: string;
  tipo: TipoDeAnotacion;
  position: Punto;
  style?: { width?: number; height?: number };
  params: Record<string, unknown>;
}

export interface LienzoGuardado {
  version: 1;
  /** Tandas en coordenadas absolutas; celdas relativas a su tanda (React Flow). */
  posiciones: Posiciones;
  /** El usuario escondió el lienzo en este chat. */
  oculto: boolean;
  /** Las tandas pedidas desde el visor, en el orden en que terminaron. */
  tandas: TandaGuardada[];
  /** Textos, notas y comentarios puestos a mano. */
  anotaciones: Anotacion[];
  /** Trazos del lápiz, en coordenadas del canvas (el mismo `Trazo` de Flujos). */
  trazos: Trazo[];
  /** Sólo en un chat que es un diseño de indumentaria
   *  (ADR-2026-09-17-el-diseno-vive-en-el-jsonb-del-lienzo). Un chat del Agente no la tiene. */
  diseno?: DisenoGuardado;
}

export const LIENZO_VACIO: LienzoGuardado = {
  version: 1,
  posiciones: {},
  oculto: false,
  tandas: [],
  anotaciones: [],
  trazos: [],
};

const esPunto = (v: unknown): v is Punto =>
  !!v &&
  typeof v === 'object' &&
  Number.isFinite((v as Punto).x) &&
  Number.isFinite((v as Punto).y);

export function normalizarLienzo(raw: unknown): LienzoGuardado {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return LIENZO_VACIO;
  const r = raw as Record<string, unknown>;
  if (r.version !== 1) return LIENZO_VACIO;
  const posiciones: Posiciones = {};
  const crudas = r.posiciones;
  if (crudas && typeof crudas === 'object' && !Array.isArray(crudas)) {
    for (const [id, p] of Object.entries(crudas as Record<string, unknown>)) {
      if (esPunto(p)) posiciones[id] = { x: p.x, y: p.y };
    }
  }
  const diseno = normalizarDiseno(r.diseno);
  return {
    version: 1,
    posiciones,
    oculto: r.oculto === true,
    tandas: tandasDe(r.tandas),
    anotaciones: anotacionesDe(r.anotaciones),
    trazos: trazosDe(r.trazos),
    ...(diseno ? { diseno } : {}),
  };
}

const esTipoDeAnotacion = (v: unknown): v is TipoDeAnotacion =>
  typeof v === 'string' && (TIPOS_DE_ANOTACION as readonly string[]).includes(v);

/** Sólo las anotaciones bien formadas; el `style` se queda con lo que sea número. */
function anotacionesDe(raw: unknown): Anotacion[] {
  if (!Array.isArray(raw)) return [];
  const out: Anotacion[] = [];
  for (const a of raw) {
    if (!a || typeof a !== 'object') continue;
    const r = a as Record<string, unknown>;
    if (!esTexto(r.id) || !esTipoDeAnotacion(r.tipo) || !esPunto(r.position)) continue;
    const params = r.params && typeof r.params === 'object' && !Array.isArray(r.params) ? (r.params as Record<string, unknown>) : {};
    const st = r.style as { width?: unknown; height?: unknown } | null | undefined;
    const style: Anotacion['style'] = {};
    if (st && typeof st === 'object') {
      if (Number.isFinite(st.width)) style.width = st.width as number;
      if (Number.isFinite(st.height)) style.height = st.height as number;
    }
    out.push({
      id: r.id,
      tipo: r.tipo,
      position: { x: r.position.x, y: r.position.y },
      ...(Object.keys(style).length > 0 ? { style } : {}),
      params,
    });
  }
  return out;
}

/** Sólo los trazos con puntos válidos (pares de números): un trazo roto no dibuja nada. */
function trazosDe(raw: unknown): Trazo[] {
  if (!Array.isArray(raw)) return [];
  const out: Trazo[] = [];
  for (const t of raw) {
    if (!t || typeof t !== 'object') continue;
    const r = t as Record<string, unknown>;
    if (!esTexto(r.id) || !Array.isArray(r.puntos)) continue;
    const puntos = r.puntos.filter((n): n is number => Number.isFinite(n));
    if (puntos.length < 2 || puntos.length % 2 !== 0) continue;
    out.push({
      id: r.id,
      autorId: typeof r.autorId === 'string' ? r.autorId : '',
      autorNombre: typeof r.autorNombre === 'string' ? r.autorNombre : '',
      color: typeof r.color === 'string' ? r.color : '#1644EB', // hex-de-marca-ok: color persistido en el lienzo
      ancho: Number.isFinite(r.ancho) && (r.ancho as number) > 0 ? (r.ancho as number) : 4,
      puntos,
      creadoEn: typeof r.creadoEn === 'string' ? r.creadoEn : '',
    });
  }
  return out;
}

const esTexto = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

/** Sólo las tandas bien formadas; una rota se descarta sola, no arrastra al resto. */
function tandasDe(raw: unknown): TandaGuardada[] {
  if (!Array.isArray(raw)) return [];
  const out: TandaGuardada[] = [];
  for (const t of raw) {
    if (!t || typeof t !== 'object') continue;
    const r = t as Record<string, unknown>;
    if (!esTexto(r.id) || !Array.isArray(r.imageIds)) continue;
    const imageIds = r.imageIds.filter(esTexto);
    const p = r.pendiente as
      | { total?: unknown; executionId?: unknown; executionIds?: unknown; pedido?: unknown }
      | null
      | undefined;
    const executionIds = Array.isArray(p?.executionIds) ? p.executionIds.filter(esTexto) : [];
    const pedido = normalizarPedido(p?.pedido);
    const pendiente =
      p && typeof p === 'object' && typeof p.total === 'number' && p.total > 0
        ? {
            total: Math.floor(p.total),
            ...(esTexto(p.executionId) ? { executionId: p.executionId } : {}),
            ...(executionIds.length > 0 ? { executionIds } : {}),
            ...(pedido ? { pedido } : {}),
          }
        : undefined;
    // Sin fotos y sin nada que esperar no es una tanda.
    if (imageIds.length === 0 && !pendiente) continue;
    out.push({
      id: r.id,
      titulo: typeof r.titulo === 'string' ? r.titulo : '',
      creadaEn: Number.isFinite(r.creadaEn) ? (r.creadaEn as number) : 0,
      ratio: Number.isFinite(r.ratio) && (r.ratio as number) > 0 ? (r.ratio as number) : 0.8,
      esVideo: r.esVideo === true,
      ...(esTexto(r.origenId) ? { origenId: r.origenId } : {}),
      ...(esPunto(r.posicion) ? { posicion: { x: r.posicion.x, y: r.posicion.y } } : {}),
      imageIds,
      ...(pendiente ? { pendiente } : {}),
    });
  }
  return out;
}

/** El pedido guardado de una tanda pendiente, o `undefined` si no está bien formado. */
function normalizarPedido(raw: unknown): PedidoGuardado | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  if (!(ACCIONES_DE_DISENO as readonly string[]).includes(String(r.accion))) return undefined;
  if (typeof r.cantidad !== 'number' || !(r.cantidad > 0)) return undefined;
  const partes = Array.isArray(r.partes)
    ? r.partes
        .filter((x): x is { celdaId: string; instruccion?: unknown; marca?: unknown } => !!x && typeof x === 'object' && esTexto((x as { celdaId?: unknown }).celdaId))
        .map((x) => {
          const marca = normalizarMarca(x.marca);
          return { celdaId: x.celdaId, instruccion: typeof x.instruccion === 'string' ? x.instruccion : '', ...(marca ? { marca } : {}) };
        })
    : [];
  const marcaBase = normalizarMarca(r.marcaBase);
  return {
    accion: r.accion as AccionDeDiseno,
    cantidad: Math.floor(r.cantidad),
    ...(esTexto(r.celdaId) ? { celdaId: r.celdaId } : {}),
    ...(partes.length > 0 ? { partes } : {}),
    ...(esTexto(r.instruccion) ? { instruccion: r.instruccion } : {}),
    ...(esTexto(r.instruccionGeneral) ? { instruccionGeneral: r.instruccionGeneral } : {}),
    ...((RESULTADOS_DE_MEZCLA as readonly string[]).includes(String(r.resultado)) ? { resultado: r.resultado as ResultadoDeMezcla } : {}),
    ...(marcaBase ? { marcaBase } : {}),
    ...(esTexto(r.textoBase) ? { textoBase: r.textoBase } : {}),
  };
}

export function conPosiciones(l: LienzoGuardado, nuevas: Posiciones): LienzoGuardado {
  return { ...l, posiciones: { ...l.posiciones, ...nuevas } };
}

export function conTanda(l: LienzoGuardado, t: TandaGuardada): LienzoGuardado {
  return { ...l, tandas: [...l.tandas, t] };
}

/** La tanda ya tiene sus fotos: deja de estar pendiente. */
export function conTandaResuelta(l: LienzoGuardado, id: string, imageIds: string[]): LienzoGuardado {
  return {
    ...l,
    tandas: l.tandas.map((t) => {
      if (t.id !== id) return t;
      const { pendiente: _p, ...resto } = t;
      return { ...resto, imageIds };
    }),
  };
}

export function sinTanda(l: LienzoGuardado, id: string): LienzoGuardado {
  return { ...l, tandas: l.tandas.filter((t) => t.id !== id) };
}

/** El diseño, transformado; un lienzo sin diseño arranca del vacío. */
export function conDiseno(l: LienzoGuardado, fn: (d: DisenoGuardado) => DisenoGuardado): LienzoGuardado {
  return { ...l, diseno: fn(l.diseno ?? DISENO_VACIO) };
}
